import { useRef, useState } from 'react';
import { Alert, Button, Card, Input, type InputRef } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { itemsOf, listPromises, type PAPromise } from '../api/money';
import { getPromiseTerms, takePromise, type PAPromiseTerms } from '../api/payments';
import { formatInstant, formatMinor, parseMinor } from '../format';
import { Money } from '../money/Money';
import { DataTable, FactList, type Column } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import { promiseBlockedLabel, promiseStateLabel } from '../money/labels';
import { errorText } from '../session/errorText';

/**
 * «Обещанные платежи» — Appendix L §21 (the FORM, W3) above §23 (the history, W2).
 *
 * ============================================================================
 * ⚠ THE FORM OFFERS EXACTLY WHAT THE GRANT WOULD ALLOW, BECAUSE ONE FUNCTION DECIDES BOTH
 * ============================================================================
 *
 * Every figure and every refusal on the panel comes from `GET /pa/payments/promise`, which
 * is `contracts.PromiseTermsFor(…, PromiseByClient)` — literally the function
 * `GrantPromiseFor` calls to refuse. So this screen cannot offer a button the server would
 * refuse, nor refuse one it would allow, and `blocked_code` IS the refusal rather than a
 * portal's guess at it (D96's structural half).
 *
 * ⚠ IT ASKS AS THE **CLIENT**. D120(c): «allow by manager» and «allow by client» are two
 * flags in Appendix I §2.9 and were one boolean in this schema until migration 0131.
 * Asking the manager's question would offer a subscriber a button his operator
 * deliberately reserved to staff.
 *
 * ⚠ A SECOND SUBMIT READS AS THE SENTENCE IT IS. `promise_one_active` is a unique partial
 * index, so two grants cannot both land: the loser is refused `409
 * contracts.promise_active_exists`, which is also what a second deliberate attempt gets.
 * The screen shows the server's own Russian message through `errorText` and re-reads the
 * terms, so the panel that had offered the button now shows the active promise instead —
 * a double tap ends in a true screen rather than in a red one.
 *
 * WHAT IS DELIBERATELY ABSENT: a REPAY-BY PICKER (§21 draws the date as a display and
 * `POST` decodes `{amount}` alone — an editable field whose value is dropped is the D109
 * class) and the FEE WARNING (§2.7 says a fee «may be implemented as a charged service»
 * and `PromiseTermsFor` carries no fee field, so the figure would have to be invented
 * here; where an operator charges one it appears on «Расход средств» as the charge it is).
 *
 * ⚠ THE STATUS COLUMN CARRIES THE ENGINE'S FOUR STATES, ALL FIRST-CLASS. §23's own table
 * says «Действует / Отменён (paid / overdue)», with the two important ones in a parenthesis —
 * and the UI spec now records that as superseded, because those two are exactly the pair a
 * subscriber must be able to tell apart: `repaid` means he settled the promise, `expired`
 * means the allowance lapsed and may have suspended him (Amendment 01 §4.3). The server sends
 * the raw code and this screen owns the wording.
 *
 * ⚠ THERE IS NO «Комиссия» COLUMN, and that is a fact about the data rather than an omission
 * on the screen. A promised-payment fee «may be implemented as a charged service» (§2.7), so
 * where an operator charges one it is a CHARGE and appears on «Расход средств» — there is no
 * fee column on the promise row to read. A later wave that wants it here must join the charge.
 *
 * ~~There is no «take a promised payment» form either: that is a WRITE and belongs to W3.~~
 * *(struck 2026-08-12 — W3 built it, above the history.)*
 */
export function PromisesScreen() {
  const { t } = useTranslation();
  return (
    <MoneyPage titleKey="promises.title" leadKey="promises.lead">
      {(contract) => (
        <>
          <PromiseForm contractID={contract.id} currency={contract.currency} />
          <h2 className="pa-section-title">{t('promises.historyTitle')}</h2>
          <PromisesBody contractID={contract.id} currency={contract.currency} />
        </>
      )}
    </MoneyPage>
  );
}

/**
 * §21's form — the terms, the request and its refusals.
 *
 * The amount is held as the TEXT the subscriber typed and converted with `parseMinor`,
 * for the reason `PaymentScreen` states at length: holding a number of rubles would mean
 * a multiplication on submit, which is arithmetic on money, and `formatMinor` →
 * `parseMinor` round-trips exactly so «Подставить» puts the engine's own figure in the box
 * to the kopeck.
 */
function PromiseForm({ contractID, currency }: { contractID: number; currency: string }) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PAPromiseTerms>(
    () => getPromiseTerms(contractID),
    [contractID],
  );
  const [amountText, setAmountText] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [localError, setLocalError] = useState('');
  const [granted, setGranted] = useState(false);
  const amountRef = useRef<InputRef>(null);

  return (
    <QueryFrame query={query} reload={reload}>
      {(terms) => {
        // §2.9's two flags, and the sentence must not say the product does not exist: the
        // operator may offer promises through a manager and not through the cabinet.
        if (!terms.allowed) {
          return <Alert className="pa-block" type="info" showIcon message={t('promises.notAllowed')} />;
        }

        const submit = async () => {
          setFailure(null);
          setLocalError('');
          const value = parseMinor(amountText);
          if (value === null || value <= 0) {
            setLocalError(t('promises.amountInvalid'));
            amountRef.current?.focus();
            return;
          }
          // The bounds are the operator's, checked locally with the SAME figures the
          // server refuses with. `max_amount` 0 means «no upper bound», so it is a bound
          // only when it is non-zero — treating 0 as a ceiling would refuse every request
          // on an installation that sets none.
          if (value < terms.min_amount) {
            setLocalError(t('promises.amountBelowMin', { min: formatMinor(terms.min_amount) }));
            amountRef.current?.focus();
            return;
          }
          if (terms.max_amount > 0 && value > terms.max_amount) {
            setLocalError(
              t('promises.amountOutOfRange', {
                min: formatMinor(terms.min_amount),
                max: formatMinor(terms.max_amount),
              }),
            );
            amountRef.current?.focus();
            return;
          }
          setBusy(true);
          try {
            await takePromise(contractID, value);
            setGranted(true);
            setAmountText('');
            // Re-read the terms: the panel that offered the button now carries the active
            // promise and `can_grant: false`, which is the true screen.
            reload();
          } catch (err) {
            setFailure(err);
            // A 409 «уже есть активный» is the server telling this panel it is stale, so
            // the panel re-reads rather than leaving a button that will refuse again.
            reload();
          } finally {
            setBusy(false);
          }
        };

        return (
          <Card className="pa-block" title={t('promises.formTitle')}>
            {granted ? (
              <Alert type="success" showIcon message={t('promises.granted')} style={{ marginBottom: 16 }} />
            ) : null}
            {failure ? (
              <Alert type="error" showIcon message={errorText(failure, t)} style={{ marginBottom: 16 }} />
            ) : null}

            <FactList facts={termsFacts(terms, currency, t)} />

            {terms.can_grant ? (
              <>
                {/* §21's «Рекомендуемый платёж — click to auto-fill Amount». It COPIES the
                    engine's figure into the box (exactly, through the formatter's own
                    round trip) and computes nothing. */}
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => {
                    setLocalError('');
                    setAmountText(formatMinor(terms.recommended_amount));
                  }}
                >
                  {t('promises.recommendedUse')}
                </Button>
                <label className="pa-field-label" htmlFor="pa-promise-amount">
                  {t('promises.amountLabel')}
                </label>
                <Input
                  id="pa-promise-amount"
                  ref={amountRef}
                  className="pa-amount-input"
                  size="large"
                  inputMode="decimal"
                  autoComplete="off"
                  value={amountText}
                  placeholder={t('promises.amountPlaceholder')}
                  status={localError ? 'error' : undefined}
                  onChange={(e) => {
                    setLocalError('');
                    setAmountText(e.target.value);
                  }}
                />
                {localError ? <p className="pa-field-error">{localError}</p> : null}
                <div className="pa-actions">
                  <Button type="primary" size="large" block loading={busy} onClick={() => void submit()}>
                    {t('promises.submit')}
                  </Button>
                </div>
              </>
            ) : (
              <Alert
                type="warning"
                showIcon
                message={t('promises.blockedTitle')}
                description={promiseBlockedLabel(terms.blocked_code, t)}
              />
            )}
          </Card>
        );
      }}
    </QueryFrame>
  );
}

/**
 * The terms §21 shows beside the box. «Рекомендуемая сумма» is CLICKABLE and fills the
 * field with the engine's own figure — §21 asks for exactly that, and it is a copy rather
 * than a computation.
 */
function termsFacts(terms: PAPromiseTerms, currency: string, t: TFunction) {
  return [
    {
      key: 'balance',
      label: t('promises.balance'),
      value: <Money minor={terms.balance} currency={currency} strong colored />,
    },
    {
      key: 'min',
      label: t('promises.minAmount'),
      value: <Money minor={terms.min_amount} currency={currency} />,
    },
    {
      key: 'max',
      label: t('promises.maxAmount'),
      // ⚠ 0 MEANS «NO UPPER BOUND» AND MUST NOT RENDER AS «0,00». A zero here would
      // assert a limit that does not exist, and every request would look out of range.
      value:
        terms.max_amount > 0 ? (
          <Money minor={terms.max_amount} currency={currency} />
        ) : (
          <>{t('promises.maxAmountNone')}</>
        ),
    },
    {
      key: 'term',
      label: t('promises.termDays'),
      value: <>{t('promises.termDaysValue', { days: terms.term_days })}</>,
    },
    {
      key: 'repay',
      label: t('promises.repayBy'),
      value: <>{formatInstant(terms.default_repay_by)}</>,
      hint: t('promises.repayByHint'),
    },
    {
      key: 'recommended',
      label: t('promises.recommended'),
      value: <Money minor={terms.recommended_amount} currency={currency} />,
    },
  ];
}

function PromisesBody({ contractID, currency }: { contractID: number; currency: string }) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PAPromise[]>(
    async () => itemsOf(await listPromises(contractID)),
    [contractID],
  );

  const columns: ReadonlyArray<Column<PAPromise>> = [
    {
      key: 'granted',
      labelKey: 'promises.columnGranted',
      render: (p) => formatInstant(p.granted_at),
    },
    {
      key: 'expires',
      labelKey: 'promises.columnExpires',
      render: (p) => formatInstant(p.expires_at),
    },
    {
      key: 'amount',
      labelKey: 'promises.columnAmount',
      className: 'pa-td-right',
      render: (p) => <Money minor={p.amount} currency={currency} />,
    },
    {
      key: 'repaid',
      labelKey: 'promises.columnRepaid',
      className: 'pa-td-right',
      // The server's own `repaid_amount` and `remainder` — never `amount − repaid`, which is
      // the same subtraction that made the resume figure wrong twice (an incoming payment
      // repays a promise FIRST, so these two do not move in step with the balance).
      render: (p) => <Money minor={p.repaid_amount} currency={currency} />,
    },
    {
      key: 'remainder',
      labelKey: 'promises.columnRemainder',
      className: 'pa-td-right',
      render: (p) => <Money minor={p.remainder} currency={currency} colored />,
    },
    {
      key: 'state',
      labelKey: 'promises.columnState',
      render: (p) => (
        <span className={p.state === 'expired' ? 'pa-status pa-status-bad' : 'pa-status'}>
          {promiseStateLabel(p.state, t)}
        </span>
      ),
    },
  ];

  return (
    <QueryFrame query={query} reload={reload}>
      {(rows) => (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(p) => p.id}
          emptyKey="promises.empty"
          detail={(p) => (
            <div className="pa-row-note">
              <span>
                {p.taken_by_subscriber ? t('promises.takenBySelf') : t('promises.takenByStaff')}
              </span>
            </div>
          )}
        />
      )}
    </QueryFrame>
  );
}
